import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FilePlus, Search, CalendarDays, Lock, CheckCircle2, Trash2, ChevronDown, ChevronUp, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => { if (!d) return '---'; return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR'); };

const STATUS_VENC = {
  Pago:     { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500' },
  Pendente: { cls: 'bg-blue-100 text-blue-700 border-blue-300',         dot: 'bg-blue-500' },
  Atrasado: { cls: 'bg-red-100 text-red-700 border-red-300',            dot: 'bg-red-500' },
};

const getVariacaoInfo = (qtdePedido, qtdeEntregue) => {
  if (!qtdePedido || !qtdeEntregue) return null;
  const variacao = ((qtdeEntregue - qtdePedido) / qtdePedido) * 100;
  const abs = Math.abs(variacao);
  const sinal = variacao >= 0 ? '+' : '';
  if (abs <= 10) return { cor: 'bg-emerald-100 text-emerald-800 border-emerald-300', semaforo: '🟢', label: `${sinal}${variacao.toFixed(1)}%` };
  if (abs <= 20) return { cor: 'bg-orange-100 text-orange-800 border-orange-300', semaforo: '🟡', label: `${sinal}${variacao.toFixed(1)}%` };
  return { cor: 'bg-red-100 text-red-800 border-red-300', semaforo: '🔴', label: `${sinal}${variacao.toFixed(1)}%` };
};

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

const DateRangePicker = ({ dateStart, dateEnd, onChange, onClear }) => {
  const today = new Date();
  const [open, setOpen] = React.useState(false);
  const [viewDate, setViewDate] = React.useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selecting, setSelecting] = React.useState('start');
  const [hoverDate, setHoverDate] = React.useState(null);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();
  const toStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const parseD = (s) => s ? new Date(s + 'T00:00:00') : null;

  const handleDayClick = (day) => {
    const clicked = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const str = toStr(clicked);
    if (selecting === 'start') { onChange({ start: str, end: null }); setSelecting('end'); }
    else {
      const start = parseD(dateStart);
      if (clicked < start) onChange({ start: str, end: dateStart });
      else onChange({ start: dateStart, end: str });
      setSelecting('start'); setOpen(false);
    }
  };

  const inRange = (day) => {
    const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const s = parseD(dateStart); const e = parseD(dateEnd) || parseD(hoverDate);
    if (!s) return false;
    if (!e) return toStr(d) === dateStart;
    return d >= s && d <= e;
  };
  const isStart = (day) => toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day)) === dateStart;
  const isEnd = (day) => toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day)) === (dateEnd || hoverDate);

  const daysInMonth = getDaysInMonth(viewDate.getFullYear(), viewDate.getMonth());
  const firstDay = getFirstDayOfMonth(viewDate.getFullYear(), viewDate.getMonth());

  const labelBtn = () => {
    if (dateStart && dateEnd) return `${fmtDate(dateStart)} → ${fmtDate(dateEnd)}`;
    if (dateStart) return `A partir de ${fmtDate(dateStart)}`;
    return 'Filtrar por Data';
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1">
        <button onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-2 h-9 px-3 text-xs font-bold rounded-lg border transition-all ${dateStart ? 'bg-[#0A3D73] text-white border-[#0A3D73]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#0A3D73]'}`}>
          <Calendar size={13}/><span className="hidden sm:inline">{labelBtn()}</span>
        </button>
        {(dateStart || dateEnd) && (
          <button onClick={onClear} className="h-9 w-9 flex items-center justify-center bg-white border border-slate-200 rounded-lg hover:border-red-400 hover:text-red-500 transition-all text-slate-400">
            <X size={13}/>
          </button>
        )}
      </div>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-white border border-slate-200 shadow-2xl rounded-xl w-72 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A3D73] text-white">
            <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()-1, 1))} className="p-1 hover:bg-white/10 rounded-lg"><ChevronLeft size={14}/></button>
            <span className="text-xs font-black uppercase tracking-widest">{MESES[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
            <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()+1, 1))} className="p-1 hover:bg-white/10 rounded-lg"><ChevronRight size={14}/></button>
          </div>
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-400">{selecting === 'start' ? '① Selecione a data inicial' : '② Selecione a data final'}</p>
          </div>
          <div className="grid grid-cols-7 px-3 pt-2">
            {DIAS_SEMANA.map(d => <div key={d} className="text-center text-xs font-black text-slate-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 px-3 pb-3 gap-y-0.5">
            {Array.from({length: firstDay}).map((_,i) => <div key={`e-${i}`}/>)}
            {Array.from({length: daysInMonth}).map((_,i) => {
              const day = i+1;
              const iR = inRange(day); const iS = isStart(day); const iE = isEnd(day);
              const isT = toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day)) === toStr(today);
              return (
                <button key={day} onClick={() => handleDayClick(day)}
                  onMouseEnter={() => { if (selecting === 'end' && dateStart) setHoverDate(toStr(new Date(viewDate.getFullYear(), viewDate.getMonth(), day))); }}
                  onMouseLeave={() => setHoverDate(null)}
                  className={`h-8 w-full text-xs font-bold rounded transition-all
                    ${iS||iE ? 'bg-[#0A3D73] text-white' : ''}
                    ${iR&&!iS&&!iE ? 'bg-blue-100 text-blue-800' : ''}
                    ${!iR&&!iS&&!iE ? 'hover:bg-slate-100 text-slate-700' : ''}
                    ${isT&&!iS&&!iE ? 'ring-1 ring-[#0A3D73] text-[#0A3D73] font-black' : ''}`}>
                  {day}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-100 px-3 py-2 flex flex-wrap gap-1.5">
            {[
              {label:'Este mês', fn:()=>{const n=new Date();onChange({start:toStr(new Date(n.getFullYear(),n.getMonth(),1)),end:toStr(new Date(n.getFullYear(),n.getMonth()+1,0))});setSelecting('start');setOpen(false);}},
              {label:'Mês anterior', fn:()=>{const n=new Date();onChange({start:toStr(new Date(n.getFullYear(),n.getMonth()-1,1)),end:toStr(new Date(n.getFullYear(),n.getMonth(),0))});setSelecting('start');setOpen(false);}},
              {label:'Este ano', fn:()=>{const n=new Date();onChange({start:`${n.getFullYear()}-01-01`,end:`${n.getFullYear()}-12-31`});setSelecting('start');setOpen(false);}},
            ].map(s => (
              <button key={s.label} onClick={s.fn} className="text-xs font-bold px-2 py-1 bg-slate-100 hover:bg-[#0A3D73] hover:text-white rounded transition-all text-slate-600">{s.label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const NotasFiscais = () => {
  const { isAdmin } = useAuth();
  const [notas, setNotas] = useState([]);
  const [vencimentos, setVencimentos] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNota, setExpandedNota] = useState(null);
  const [editandoData, setEditandoData] = useState(null);
  const [novaData, setNovaData] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, dateStart, dateEnd]);

  const fetchData = async () => {
    try {
      const [notasRes, vencRes, pedidosRes, clientesRes] = await Promise.all([
        api.get('/notas-fiscais'), api.get('/vencimentos'), api.get('/pedidos'), api.get('/clientes'),
      ]);
      setNotas(notasRes.data || []);
      setVencimentos(vencRes.data || []);
      setPedidos(pedidosRes.data || []);
      setClientes(clientesRes.data || []);
    } catch { toast.error('Erro ao carregar dados financeiros'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (notaId) => {
    if (!isAdmin) return;
    if (window.confirm('Excluir esta NF e seus vencimentos?')) {
      try { await api.delete(`/notas-fiscais/${notaId}`); toast.success('NF removida!'); fetchData(); }
      catch { toast.error('Erro ao deletar NF'); }
    }
  };

  const handleMarcarPago = async (vencId) => {
    if (!isAdmin) return;
    try {
      await api.put(`/vencimentos/${vencId}`, { status: 'Pago', data_pagamento: new Date().toISOString().split('T')[0] });
      toast.success('Pagamento confirmado!'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao confirmar pagamento'); }
  };

  const handleEditarData = async (notaId) => {
    if (!novaData) return toast.error('Informe a data de emissão');
    try {
      await api.put(`/notas-fiscais/${notaId}`, { data_emissao: novaData });
      toast.success('Data atualizada!'); setEditandoData(null); setNovaData(''); fetchData();
    } catch { toast.error('Erro ao atualizar data'); }
  };

  const clienteIdMapNF = Object.fromEntries(clientes.map(c => [c.id, c]));
  const nomeNota = (nota) => clienteIdMapNF[nota.cliente_id]?.nome_fantasia || nota.cliente_nome;

  const getVencimentosDaNota = (notaId) => vencimentos.filter(v => v.nota_fiscal_id === notaId);
  const getPedidoDaNota = (nota) => pedidos.find(p => p.id === nota.pedido_id);

  const buscaPareceNumeroNF = /\d/.test(searchTerm) && /^[\d\s./-]+$/.test(searchTerm);
  const normalizarNumeroNF = (valor) => {
    const digitos = String(valor || '').replace(/\D/g, '');
    return digitos ? (digitos.replace(/^0+/, '') || '0') : '';
  };
  const numeroNFBuscado = buscaPareceNumeroNF ? normalizarNumeroNF(searchTerm) : '';
  const existeNFExata = numeroNFBuscado
    ? notas.some(n => normalizarNumeroNF(n.numero_nf) === numeroNFBuscado)
    : false;

  const notasFiltradas = notas.filter(n => {
    const s = searchTerm.trim().toLowerCase();
    const matchSearch = !s || (existeNFExata
      ? normalizarNumeroNF(n.numero_nf) === numeroNFBuscado
      : n.numero_nf?.toLowerCase().includes(s)
        || n.cliente_nome?.toLowerCase().includes(s)
        || n.numero_fabrica?.toLowerCase().includes(s)
        || n.numero_fe?.toLowerCase().includes(s));
    let matchDate = true;
    if (dateStart || dateEnd) {
      const dataRef = n.data_emissao || n.criado_em?.substring(0,10) || '';
      if (dateStart && dateEnd) matchDate = dataRef >= dateStart && dataRef <= dateEnd;
      else if (dateStart) matchDate = dataRef >= dateStart;
      else if (dateEnd) matchDate = dataRef <= dateEnd;
    }
    return matchSearch && matchDate;
  });

  const totalPages = Math.ceil(notasFiltradas.length / itemsPerPage);
  const notasPagina = notasFiltradas.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalNFs = notasFiltradas.length;
  const totalFaturado = notasFiltradas.reduce((a,n) => a+(n.valor_total||0), 0);
  const totalComissao = notasFiltradas.reduce((a,n) => a+(n.comissao_total||0), 0);
  const notasFiltradasIds = new Set(notasFiltradas.map(n => n.id));
  const vencsFiltrados = vencimentos.filter(v => notasFiltradasIds.has(v.nota_fiscal_id));
  const totalPendente = vencsFiltrados.filter(v => v.status !== 'Pago').reduce((a,v) => a+(v.comissao_calculada||0), 0);
  const totalAtrasado = vencsFiltrados.filter(v => v.status === 'Atrasado').reduce((a,v) => a+(v.comissao_calculada||0), 0);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Notas Fiscais...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">Controle Financeiro {!isAdmin && <Lock size={14} className="text-slate-400"/>}</h1>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">Notas Fiscais & Vencimentos · {notasFiltradas.length} NFs encontradas</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg h-9 px-3 gap-2 shadow-sm">
            <Search size={13} className="text-slate-400"/>
            <input placeholder="NF, cliente, pedido..." className="text-xs font-semibold bg-transparent outline-none w-44 text-slate-700" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
          </div>
          <DateRangePicker
            dateStart={dateStart} dateEnd={dateEnd}
            onChange={({start,end}) => {setDateStart(start||'');setDateEnd(end||'');}}
            onClear={() => {setDateStart('');setDateEnd('');}}
          />
          {isAdmin && (
            <Link to="/notas-fiscais/nova" className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
              <FilePlus size={14}/> Lançar NF
            </Link>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        {[
          {label:'NFs Emitidas', value:totalNFs, cor:'#374151', fundo:'#F9FAFB'},
          {label:'Total Faturado', value:`R$ ${fmt(totalFaturado)}`, cor:'#1E40AF', fundo:'#EFF6FF'},
          {label:'Comissão Total', value:`R$ ${fmt(totalComissao)}`, cor:'#166534', fundo:'#DCFCE7'},
          {label:'Comissão Pendente', value:`R$ ${fmt(totalPendente)}`, cor:'#1D4ED8', fundo:'#DBEAFE'},
          {label:'Comissão Atrasada', value:`R$ ${fmt(totalAtrasado)}`, cor: totalAtrasado>0?'#991B1B':'#374151', fundo: totalAtrasado>0?'#FEE2E2':'#F9FAFB'},
        ].map((k,i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">{k.label}</p>
            <p className="text-base font-black font-mono" style={{color:k.cor}}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                {['NF','Pedido / FE','Cliente','Representada','Valor NF','Comissão','Entrega','Parcelas','Situação'].map(h => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}
                {isAdmin && <th className="px-4 py-3 text-center">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {notasFiltradas.length === 0 ? (
                <tr><td colSpan={isAdmin?10:9}>
                  <EmptyState
                    icon={
                      <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                        <path d="M14 8h44v56l-6-4-6 4-6-4-6 4-6-4-8 4V8z" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="24" y1="24" x2="48" y2="24" stroke="#7BB8F0" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="24" y1="34" x2="48" y2="34" stroke="#7BB8F0" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="24" y1="44" x2="38" y2="44" stroke="#7BB8F0" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    }
                    title="Nenhuma nota fiscal encontrada"
                    subtitle="As notas fiscais são criadas a partir dos pedidos entregues"
                  />
                </td></tr>
              ) : notasPagina.map((nota, idx) => {
                const vencs = getVencimentosDaNota(nota.id);
                const isExpanded = expandedNota === nota.id;
                const todosPagos = vencs.length > 0 && vencs.every(v => v.status === 'Pago');
                const temAtrasado = vencs.some(v => v.status === 'Atrasado');
                const pedido = getPedidoDaNota(nota);
                const variacaoInfo = nota.qtde_entregue && pedido?.quantidade ? getVariacaoInfo(pedido.quantidade, nota.qtde_entregue) : null;
                return (
                  <React.Fragment key={nota.id}>
                    <tr className={`text-xs border-b border-slate-100 transition-colors cursor-pointer ${idx%2===0?'bg-white':'bg-slate-50/60'} hover:bg-blue-50`}
                      onClick={() => setExpandedNota(isExpanded ? null : nota.id)}>
                      <td className="px-4 py-3">
                        <p className="font-black text-[#0A3D73] font-mono">NF {nota.numero_nf}</p>
                        {editandoData === nota.id ? (
                          <div className="flex items-center gap-1 mt-1" onClick={e => e.stopPropagation()}>
                            <input type="date" value={novaData} onChange={e => setNovaData(e.target.value)} className="text-xs border border-blue-400 rounded px-1 h-6 bg-white outline-none" autoFocus/>
                            <button onClick={() => handleEditarData(nota.id)} className="text-xs font-black text-white bg-blue-600 hover:bg-blue-700 px-1.5 h-6 rounded">✓</button>
                            <button onClick={() => { setEditandoData(null); setNovaData(''); }} className="text-xs font-black text-slate-500 hover:text-red-500 px-1 h-6">✕</button>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 font-semibold mt-0.5 cursor-pointer hover:text-blue-500 flex items-center gap-1"
                            onClick={e => { e.stopPropagation(); setEditandoData(nota.id); setNovaData(nota.data_emissao || ''); }}>
                            {fmtDate(nota.data_emissao || nota.criado_em?.substring(0,10))}
                            {nota.data_emissao ? <span className="text-blue-400">✓</span> : <span className="text-orange-400">✎</span>}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-black text-slate-700">{nota.numero_fabrica||'— aguardando —'}</p>
                        <p className="text-xs text-[#0A3D73] font-bold font-mono">{nota.numero_fe||'---'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-bold uppercase max-w-[160px] truncate">{nomeNota(nota)}</p>
                        <p className="text-xs text-slate-400 font-semibold mt-0.5 max-w-[160px] truncate">{nota.item_nome}</p>
                      </td>
                      <td className="px-4 py-3">
                        {nota.representada_nome
                          ? <span className="block max-w-[100px] truncate text-xs font-bold text-[#0A3D73]" title={nota.representada_nome}>{nota.representada_nome}</span>
                          : <span className="text-slate-300 font-bold text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-right font-black whitespace-nowrap">R$ {fmt(nota.valor_total)}</td>
                      <td className="px-4 py-3 text-right font-black text-emerald-700 whitespace-nowrap">
                        R$ {fmt(nota.comissao_total)}
                        <p className="text-xs text-slate-400 font-semibold">{fmt(nota.comissao_percent)}%</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {variacaoInfo ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`px-2 py-0.5 text-xs font-black border rounded-full ${variacaoInfo.cor}`}>{variacaoInfo.semaforo} {variacaoInfo.label}</span>
                            <span className="text-xs text-slate-400 font-semibold font-mono">{nota.qtde_entregue?.toLocaleString('pt-BR')} / {pedido?.quantidade?.toLocaleString('pt-BR')}</span>
                          </div>
                        ) : <span className="text-slate-300 font-bold">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center font-black text-slate-600">{nota.numero_parcelas}x</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 text-xs font-black border rounded-full ${todosPagos?'bg-emerald-100 text-emerald-700 border-emerald-300':temAtrasado?'bg-red-100 text-red-700 border-red-300':'bg-blue-100 text-blue-700 border-blue-300'}`}>
                          {todosPagos?'✓ Quitado':temAtrasado?'Atrasado':'Pendente'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setExpandedNota(isExpanded?null:nota.id)} className="w-7 h-7 flex items-center justify-center hover:bg-slate-100 text-slate-400 rounded-lg transition-colors">
                              {isExpanded ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                            </button>
                            <button onClick={() => handleDelete(nota.id)} className="w-7 h-7 flex items-center justify-center hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg transition-colors">
                              <Trash2 size={13}/>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={isAdmin?10:9} className="p-0 border-b border-slate-200">
                          <div className="bg-slate-50 border-t border-slate-100">
                            <div className="px-5 py-2 border-b border-slate-200 flex items-center gap-2">
                              <CalendarDays size={12} className="text-[#0A3D73]"/>
                              <p className="text-xs font-black uppercase tracking-wide text-slate-600">Cronograma de Vencimentos</p>
                            </div>
                            <table className="w-full">
                              <thead>
                                <tr className="text-xs font-black uppercase text-slate-400 border-b border-slate-200">
                                  {['Parcela','Vencimento','Valor Parcela','Comissão','Status','Pago em'].map(h => <th key={h} className="px-6 py-2">{h}</th>)}
                                  {isAdmin && <th className="px-6 py-2 text-center">Ação</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {vencs.map(v => {
                                  const cfg = STATUS_VENC[v.status] || STATUS_VENC.Pendente;
                                  return (
                                    <tr key={v.id} className="border-b border-slate-100 text-xs">
                                      <td className="px-6 py-2.5 font-black text-slate-600">{v.parcela}ª / {v.total_parcelas}</td>
                                      <td className="px-6 py-2.5 font-bold text-slate-700">{fmtDate(v.data_vencimento)}</td>
                                      <td className="px-6 py-2.5 font-bold">R$ {fmt(v.valor_parcela)}</td>
                                      <td className="px-6 py-2.5 font-black text-emerald-700">R$ {fmt(v.comissao_calculada)}</td>
                                      <td className="px-6 py-2.5">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-black border rounded-full ${cfg.cls}`}>
                                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}/>{v.status}
                                        </span>
                                      </td>
                                      <td className="px-6 py-2.5 text-xs text-slate-400 font-semibold">{v.data_pagamento ? fmtDate(v.data_pagamento) : '---'}</td>
                                      {isAdmin && (
                                        <td className="px-6 py-2.5 text-center">
                                          {v.status !== 'Pago' ? (
                                            <button onClick={() => handleMarcarPago(v.id)} className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase px-3 py-1.5 rounded-lg transition-colors mx-auto">
                                              <CheckCircle2 size={10}/> Confirmar
                                            </button>
                                          ) : <CheckCircle2 size={16} className="text-emerald-500 mx-auto"/>}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* PAGINAÇÃO */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">Exibir:</span>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-slate-200 rounded-lg text-xs font-bold bg-white px-2 h-8 outline-none focus:border-[#0A3D73] cursor-pointer">
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronLeft size={14}/>
              </button>
              <span className="text-xs font-black text-slate-600">
                Página {currentPage} de {totalPages} · {notasFiltradas.length} registros
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotasFiscais;
